/**
 * Pure, dependency-free helpers for consuming the resumable background chat
 * stream (`/api/chat/bg-stream/{chatId}?cursor=N`). Kept out of Playground.tsx
 * so the fiddly SSE-frame parsing + resilience policy is unit-testable in
 * isolation (see streamConsumer.test.ts).
 *
 * The backend tees EVERY generation frame (text deltas, tool actions,
 * deep_research_*, quota, end) into a durable per-chat event log and stamps
 * each non-terminal frame with `id: <cursor>`. A client tracks the id of the
 * last frame it applied and reconnects with `?cursor=<id>` to resume exactly
 * where it left off — so the parser must read `event:`/`data:`/`id:` lines,
 * not just `data:`.
 */

export interface SSEFrame {
  event: string;
  data: string;
  id: string | null;
}

/**
 * Split a (possibly partial) text buffer into complete SSE frames. Frames are
 * separated by a blank line. Returns the parsed frames plus any trailing
 * incomplete text that must be carried over to the next read.
 */
export function parseSSEFrames(buffer: string): { frames: SSEFrame[]; rest: string } {
  const normalized = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const blocks = normalized.split("\n\n");
  const rest = blocks.pop() ?? ""; // last block may be incomplete
  const frames: SSEFrame[] = [];
  for (const block of blocks) {
    const frame = parseFrameBlock(block);
    if (frame) frames.push(frame);
  }
  return { frames, rest };
}

function parseFrameBlock(block: string): SSEFrame | null {
  let event = "message";
  let id: string | null = null;
  const dataLines: string[] = [];
  let sawField = false;

  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue; // blank line or comment
    const idx = line.indexOf(":");
    const field = idx === -1 ? line : line.slice(0, idx);
    let value = idx === -1 ? "" : line.slice(idx + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "data") {
      dataLines.push(value);
      sawField = true;
    } else if (field === "event") {
      event = value || "message";
      sawField = true;
    } else if (field === "id") {
      id = value;
      sawField = true;
    }
    // retry / unknown fields are ignored
  }

  if (!sawField) return null;
  return { event, data: dataLines.join("\n"), id };
}

/**
 * Safely parse a stream frame's `data` payload as JSON.
 *
 * When a generation stream drops mid-frame, the SSE loader flushes the
 * trailing *incomplete* event block (see sse.js `_onStreamLoaded`), so a
 * consumer can be handed a truncated payload such as `{"t`. Calling
 * `JSON.parse` on that throws `SyntaxError: Unterminated string in JSON` and,
 * when unguarded, crashes the entire chat view (Sentry CHAT-MARITACA-1Z).
 *
 * Returns the parsed value, or `null` when the payload is empty or not valid
 * JSON. Callers must skip a `null` result rather than letting the parse throw.
 */
export function parseStreamData(data: string): any | null {
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// --- Resilience policy ------------------------------------------------------

/** Stall watchdog: declare the stream dead after this much silence. The server
 *  pings every 0.5s, so 25s == 50 missed pings — unambiguously stalled. */
export const INACTIVITY_TIMEOUT_MS = 25000;

/** Backoff schedule for auto-reconnect attempts (ms). Length == max attempts. */
export const RECONNECT_BACKOFF_MS = [2000, 5000, 10000];
export const MAX_RECONNECT_ATTEMPTS = RECONNECT_BACKOFF_MS.length;

export function getReconnectDelayMs(attempt: number): number {
  const i = Math.max(0, Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1));
  return RECONNECT_BACKOFF_MS[i];
}

/**
 * Whether an SSE error is transient (worth auto-reconnecting from) vs. a fatal
 * application error that should surface to the user. status 0/undefined is a
 * network drop or our synthetic stall; 5xx gateway codes are transient proxy
 * blips; auth/quota/validation (4xx) are fatal.
 */
export function isTransientError(status: number | undefined | null): boolean {
  if (status === undefined || status === null || status === 0) return true;
  return status === 502 || status === 503 || status === 504;
}

export function buildBgStreamUrl(apiBase: string, chatId: string, cursor: number): string {
  const c = Number.isFinite(cursor) && cursor > 0 ? Math.floor(cursor) : 0;
  return `${apiBase}/api/chat/bg-stream/${encodeURIComponent(chatId)}?cursor=${c}`;
}

// --- Frame classification ---------------------------------------------------

export const DEEP_RESEARCH_EVENTS = [
  "deep_research_status",
  "deep_research_plan",
  "deep_research_step",
  "deep_research_metadata",
];

export function isDeepResearchEvent(event: string): boolean {
  return DEEP_RESEARCH_EVENTS.indexOf(event) !== -1;
}

export function isTerminalEvent(event: string): boolean {
  return event === "end";
}

/** Parse the `id` of a frame into a resume cursor, falling back to `prev`. */
export function cursorFromFrame(frame: SSEFrame, prev: number): number {
  if (frame.id == null || frame.id === "") return prev;
  const n = parseInt(frame.id, 10);
  return Number.isFinite(n) && n > prev ? n : prev;
}
